import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider.js';
import { getTokenExpiresAt, setAccessToken } from './session.js';
import {
  IMPERSONATION_HANDOFF_READY,
  impersonationClaimFromToken,
  impersonationViewFromToken,
  isImpersonationHandoffTokenMessage,
  markImpersonationGrantEnded,
  shouldIgnoreImpersonationHandoff,
} from './impersonation.js';

/**
 * 10-03 / 07-08: banner on the impersonated subject app. Exit has no confirm.
 * Back cannot restore an ended grant (handoff of the same grantId is ignored).
 */
export function SubjectImpersonationBanner() {
  const { accessToken, logout, signInPath } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (window.opener) {
      window.opener.postMessage({ type: IMPERSONATION_HANDOFF_READY }, '*');
    }
    function onMessage(event: MessageEvent) {
      if (!isImpersonationHandoffTokenMessage(event.data)) return;
      const claim = impersonationClaimFromToken(event.data.accessToken);
      const grantId = claim?.grantId ?? claim?.ref;
      if (shouldIgnoreImpersonationHandoff(grantId)) return;
      setAccessToken(event.data.accessToken);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const view = useMemo(
    () => impersonationViewFromToken(accessToken, getTokenExpiresAt()),
    [accessToken, now],
  );

  if (!view) return null;

  const remainingMs = view.expiresAt ? Date.parse(view.expiresAt) - now : null;
  const remainingLabel =
    remainingMs != null && Number.isFinite(remainingMs)
      ? remainingMs <= 0
        ? 'expired'
        : `${Math.max(1, Math.ceil(remainingMs / 60000))} min remaining`
      : null;
  const grantId = view.grantId;

  async function onExit() {
    markImpersonationGrantEnded(grantId);
    await logout();
    navigate(signInPath, { replace: true });
  }

  return (
    <div
      role="region"
      aria-label="Impersonation"
      aria-live="assertive"
      data-testid="subject-impersonation-banner"
      className="sticky top-0 z-[var(--z-impersonation)] flex flex-wrap items-center gap-inline border-l-4 border-blocked bg-blocked px-field py-inline text-body text-inverse"
    >
      <strong>{view.readOnly ? 'IMPERSONATING' : 'IMPERSONATING WITH WRITE'}</strong>
      <span>Support session · {view.actorLabel}</span>
      {remainingLabel ? <span>{remainingLabel}</span> : null}
      <span>{view.readOnly ? 'Read-only.' : 'Each change requires a reason.'}</span>
      <button type="button" className="underline" onClick={() => void onExit()}>
        End now
      </button>
    </div>
  );
}
